import React, { useEffect, useState } from "react";
import {
  addCoupon,
  addPhoneNumber,
  deleteCoupon,
  deletePhoneNumber,
  getAllCoupons,
  getPhoneNumbers,
} from "../../actions/admin";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Grid,
  IconButton,
  TextField,
  Typography,
  styled,
  Button,
  FormHelperText,
  Backdrop,
  CircularProgress,
  Card,
  CardContent,
  Container,
  Fade,
  Zoom,
  Snackbar,
  Alert,
} from "@mui/material";
import { DeleteForever, Phone, Add, ContactPhone } from "@mui/icons-material";
import { useForm } from "react-hook-form";

const StyledContainer = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  paddingTop: "2rem",
  paddingBottom: "2rem",
  width: "100%",
}));

const GlassCard = styled(Card)(({ theme }) => ({
  background: "rgba(255, 255, 255, 0.98)",
  backdropFilter: "blur(20px)",
  borderRadius: "20px",
  border: "1px solid rgba(255, 255, 255, 0.3)",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.15)",
  transition: "all 0.3s ease-in-out",
  "&:hover": {
    transform: "translateY(-5px)",
    boxShadow: "0 12px 40px rgba(0, 0, 0, 0.2)",
  },
}));

const ContactCard = styled(Card)(({ theme }) => ({
  background: "rgba(255, 255, 255, 0.95)",
  backdropFilter: "blur(10px)",
  borderRadius: "12px",
  border: "1px solid rgba(255, 255, 255, 0.2)",
  boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  position: "relative",
  overflow: "hidden",
  width: "100%",
  height: "fit-content",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
  },
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "3px",
    background: "linear-gradient(90deg, #667eea 0%, #764ba2 100%)",
  },
}));

const FormCard = styled(GlassCard)(({ theme }) => ({
  padding: "2rem",
  maxWidth: "500px",
  margin: "0 auto",
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    background: "rgba(255, 255, 255, 0.8)",
    "& fieldset": {
      borderColor: "rgba(103, 126, 234, 0.3)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(103, 126, 234, 0.5)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#667eea",
      borderWidth: "2px",
    },
  },
  "& .MuiInputLabel-root": {
    color: "#667eea",
    fontWeight: "500",
  },
}));

const NumberTextField = styled(StyledTextField)({
  "& input": {
    height: "40px",
    padding: "0px 14px !important",
    fontSize: "1rem",
  },
});

const GradientButton = styled(Button)(({ theme }) => ({
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  borderRadius: "12px",
  padding: "12px 24px",
  textTransform: "none",
  fontSize: "1rem",
  fontWeight: "600",
  boxShadow: "0 4px 15px rgba(103, 126, 234, 0.4)",
  transition: "all 0.3s ease",
  "&:hover": {
    background: "linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)",
    transform: "translateY(-2px)",
    boxShadow: "0 6px 20px rgba(103, 126, 234, 0.6)",
  },
}));

const DeleteButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: "12px",
  right: "12px",
  background: "rgba(244, 67, 54, 0.1)",
  color: "#f44336",
  "&:hover": {
    background: "rgba(244, 67, 54, 0.2)",
    transform: "scale(1.1)",
  },
  transition: "all 0.2s ease",
}));

const Label = styled(Typography)({
  marginBottom: "8px",
  fontWeight: "600",
  color: "#374151",
  fontSize: "0.95rem",
});

const SectionTitle = styled(Typography)(({ theme }) => ({
  color: "white",
  fontWeight: "700",
  marginBottom: "2rem",
  textAlign: "center",
  textShadow: "0 2px 4px rgba(0, 0, 0, 0.3)",
}));

const ContactNumbers = () => {
  const { token } = useSelector((state) => state.userSlice);
  const dispatch = useDispatch();
  const { phone_numbers } = useSelector((state) => state.appSlice);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      name: "",
      number: "",
    },
  });

  const deleteContact = async (id) => {
    setLoading(true);
    try {
      await deletePhoneNumber({ token, id });
      dispatch(getPhoneNumbers());
      setToast({
        open: true,
        message: "Contact deleted successfully!",
        severity: "success",
      });
    } catch (error) {
      setToast({
        open: true,
        message: "Failed to delete contact",
        severity: "error",
      });
    }
    setLoading(false);
  };

  const addContact = async (data) => {
    setLoading(true);
    try {
      await addPhoneNumber({ token, data });
      dispatch(getPhoneNumbers());
      reset();
      setToast({
        open: true,
        message: "Contact added successfully!",
        severity: "success",
      });
    } catch (error) {
      setToast({
        open: true,
        message: "Failed to add contact",
        severity: "error",
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    dispatch(getPhoneNumbers());
  }, []);

  return (
    <StyledContainer>
      <Container maxWidth="xl">
        <Backdrop
          open={loading}
          sx={{
            zIndex: 1010,
            backdropFilter: "blur(4px)",
          }}
        >
          <CircularProgress size={60} thickness={4} sx={{ color: "#667eea" }} />
        </Backdrop>

        <Grid container spacing={4}>
          {/* All Contact Numbers Section */}
          <Grid item xs={12} lg={6}>
            <SectionTitle variant="h4">
              <ContactPhone sx={{ mr: 1, verticalAlign: "middle" }} />
              All Contact Numbers
            </SectionTitle>

            <Grid container spacing={2}>
              {phone_numbers.map((contact, index) => (
                <Grid item xs={12} key={contact._id}>
                  <Zoom
                    in={true}
                    style={{ transitionDelay: `${index * 100}ms` }}
                  >
                    <ContactCard>
                      <DeleteButton
                        onClick={() => deleteContact(contact._id)}
                        size="small"
                      >
                        <DeleteForever />
                      </DeleteButton>

                      <CardContent sx={{ p: 2, pr: 5 }}>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 2 }}
                        >
                          <Phone
                            sx={{ color: "#667eea", fontSize: "1.2rem" }}
                          />
                          <Box>
                            <Typography
                              variant="h6"
                              sx={{
                                textTransform: "capitalize",
                                fontWeight: "600",
                                color: "#1f2937",
                                fontSize: "1.1rem",
                                mb: 0.5,
                              }}
                            >
                              {contact.name}
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{
                                fontWeight: "600",
                                color: "#667eea",
                                fontSize: "0.95rem",
                              }}
                            >
                              {contact.number}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </ContactCard>
                  </Zoom>
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Add New Contact Section */}
          <Grid item xs={12} lg={6}>
            <SectionTitle variant="h4">
              <Add sx={{ mr: 1, verticalAlign: "middle" }} />
              Add New Contact Number
            </SectionTitle>

            <Fade in={true} timeout={800}>
              <FormCard>
                <CardContent>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Label>Name</Label>
                      <StyledTextField
                        fullWidth
                        placeholder="Enter the name"
                        {...register("name", { required: "Name is required!" })}
                        error={!!errors?.name}
                      />
                      {errors?.name && (
                        <FormHelperText error sx={{ mt: 1 }}>
                          {errors?.name?.message}
                        </FormHelperText>
                      )}
                    </Grid>

                    <Grid item xs={12}>
                      <Label>Contact Number</Label>
                      <StyledTextField
                        fullWidth
                        placeholder="Enter the contact number"
                        InputProps={{
                          startAdornment: (
                            <Phone sx={{ color: "#667eea", mr: 1 }} />
                          ),
                        }}
                        {...register("number", {
                          required: "Contact number is required!",
                        })}
                        error={!!errors?.number}
                      />
                      {errors?.number && (
                        <FormHelperText error sx={{ mt: 1 }}>
                          {errors?.number?.message}
                        </FormHelperText>
                      )}
                    </Grid>

                    <Grid item xs={12}>
                      <GradientButton
                        fullWidth
                        variant="contained"
                        size="large"
                        onClick={handleSubmit(addContact)}
                        startIcon={<Add />}
                      >
                        Add Contact
                      </GradientButton>
                    </Grid>
                  </Grid>
                </CardContent>
              </FormCard>
            </Fade>
          </Grid>
        </Grid>

        {/* Toast Notification */}
        <Snackbar
          open={toast.open}
          autoHideDuration={4000}
          onClose={() => setToast({ ...toast, open: false })}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={() => setToast({ ...toast, open: false })}
            severity={toast.severity}
            variant="filled"
            sx={{
              borderRadius: "12px",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
            }}
          >
            {toast.message}
          </Alert>
        </Snackbar>
      </Container>
    </StyledContainer>
  );
};

export default ContactNumbers;
